import { Button, Grid, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';

import { Icon, Loading, Mask } from '@hapvida/hapvida-core-components';

import { UserStatusEnum } from '@hapvida/hapvida-core-components/src/components/NewFilterSummary/constants';
import { NEW_COLORS } from '@hapvida/hapvida-core-components/src/themes/colors/newColors';

import {
  USER_STATUS_COLOR,
  USER_STATUS_ICON_NAME,
  USER_STATUS_NAME,
} from '@constants';
import { CreateBackofficeUserRouter } from '@flows/private/backofficeUser/pages/BackofficeUserCreation/router';
import { BackofficeUserDetailsRouter } from '@flows/private/backofficeUser/pages/BackofficeUserDetails/router';

import { useReprocessUserBackofficeMobile } from '../../hooks/useReprocessUserBackoffice/useReprocessUserBackoffice';
import {
  ActionButton,
  ErrorBox,
  Line,
  ReprocessButton,
  StyledGrid,
} from './styles';
import type { UserListMobileProps } from './types';

export function UserBackofficeListCardMobile({
  usersListData,
  hasNextPage,
  fetchNextPage,
}: Readonly<UserListMobileProps>) {
  const navigateTo = useNavigate();
  const { loadingState, handleReprocessUser } =
    useReprocessUserBackofficeMobile();

  const usersData =
    usersListData?.pages?.map(user => user?.users)?.flat() ?? [];

  const handleEdit = useCallback(
    (id: string) => {
      navigateTo(BackofficeUserDetailsRouter.dynamicPath({ id }));
    },
    [navigateTo],
  );

  return (
    <>
      <Button
        variant="contained"
        color="success"
        onClick={() => navigateTo(CreateBackofficeUserRouter.path)}
      >
        Novo usuário backoffice
      </Button>
      <InfiniteScroll
        dataLength={usersData?.length}
        hasMore={!!hasNextPage}
        next={fetchNextPage}
        loader={<Loading animationProps={{ height: 20, width: 20 }} />}
        height={'800px'}
      >
        <Grid container gap={1} flexDirection="row" flexWrap="wrap">
          {usersData?.map(user => {
            const isError = user?.status === UserStatusEnum.ERROR;

            return (
              <Grid item xs={12} key={user?.id}>
                <Line container alignItems="center" hasError={isError}>
                  <Stack
                    flexDirection="row"
                    alignItems="flex-start"
                    mb={2}
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Stack
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      gap={1}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Icon
                          name={USER_STATUS_ICON_NAME[user?.status]}
                          color={USER_STATUS_COLOR[user?.status]}
                          size={20}
                        />
                        <Typography
                          fontWeight={600}
                          variant="smallText"
                          color={USER_STATUS_COLOR[user?.status]}
                        >
                          {user?.status ? USER_STATUS_NAME[user?.status] : '-'}
                        </Typography>
                      </Stack>
                      <Stack>
                        <Typography fontWeight={700} variant="subtitle2">
                          {user?.name}
                        </Typography>
                        {user?.document && (
                          <Typography variant="smallText">
                            {Mask.cpf(user?.document) ?? '-'}
                          </Typography>
                        )}
                      </Stack>
                      <Typography
                        variant="body2"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {user?.email}
                      </Typography>
                    </Stack>
                  </Stack>
                  <StyledGrid item xs={12}>
                    {isError ? (
                      <ErrorBox>
                        <Typography
                          color={NEW_COLORS.SEMANTICS.ERROR[600]}
                          variant="text"
                          pl={2}
                        >
                          {' '}
                          {user?.notification}
                        </Typography>
                        {user?.allowResend && (
                          <ReprocessButton
                            disabled={loadingState}
                            onClick={() => handleReprocessUser(user?.id)}
                            endIcon={
                              <Icon
                                name="actions-b2b-refresh"
                                color={NEW_COLORS.BASE.WHITE}
                              />
                            }
                            variant="contained"
                            color="secondary"
                            size="small"
                          >
                            <Typography
                              fontSize={16}
                              color={NEW_COLORS.BASE.WHITE}
                            >
                              Reprocessar
                            </Typography>
                          </ReprocessButton>
                        )}
                      </ErrorBox>
                    ) : (
                      <ActionButton
                        onClick={() => handleEdit(user?.id)}
                        color="secondary"
                        variant="contained"
                        size="small"
                        fullWidth
                        endIcon={
                          <Icon
                            color={NEW_COLORS.BASE.WHITE}
                            name="actions-b2b-expand-circle-right"
                          />
                        }
                      >
                        <Typography
                          fontSize={16}
                          color={NEW_COLORS.BASE.WHITE}
                          mr={'8px'}
                        >
                          Ver detalhes
                        </Typography>
                      </ActionButton>
                    )}
                  </StyledGrid>
                </Line>
              </Grid>
            );
          })}
        </Grid>
      </InfiniteScroll>
    </>
  );
}
